import { Language, PlanetName, ZodiacSign } from '../types/kundali';
import {
  SUPPORTED_LANGUAGES,
  PLANETS_SA,
  ZODIAC_SA,
  TERMS_SA,
  PLANETS_PA,
  ZODIAC_PA,
  TERMS_PA
} from '../constants/languages';

export {
  SUPPORTED_LANGUAGES,
  PLANETS_SA,
  ZODIAC_SA,
  TERMS_SA,
  PLANETS_PA,
  ZODIAC_PA,
  TERMS_PA
};

// 2-3 letter lowercase planet codes for English, and script short codes for Hindi / Sanskrit / Punjabi
export function getPlanetShortCode(planetName: PlanetName | string, lang: Language = 'en'): string {
  if (lang === 'sa') {
    switch (planetName) {
      case 'Sun': return 'सू';
      case 'Moon': return 'चं';
      case 'Mars': return 'मं';
      case 'Mercury': return 'बु';
      case 'Jupiter': return 'गु';
      case 'Venus': return 'शु';
      case 'Saturn': return 'श';
      case 'Rahu': return 'रा';
      case 'Ketu': return 'के';
      case 'Ascendant':
      case 'Lagna':
      case 'ASC': return 'लग्न';
      default: return (PLANETS_SA[planetName] || planetName).slice(0, 2);
    }
  }

  if (lang === 'pa') {
    switch (planetName) {
      case 'Sun': return 'ਸੂਰ';
      case 'Moon': return 'ਚੰਦ';
      case 'Mars': return 'ਮੰਗ';
      case 'Mercury': return 'ਬੁੱਧ';
      case 'Jupiter': return 'ਗੁਰੂ';
      case 'Venus': return 'ਸ਼ੁੱਕ';
      case 'Saturn': return 'ਸ਼ਨੀ';
      case 'Rahu': return 'ਰਾਹੂ';
      case 'Ketu': return 'ਕੇਤੂ';
      case 'Ascendant':
      case 'Lagna':
      case 'ASC': return 'ਲਗ';
      default: return (PLANETS_PA[planetName] || planetName).slice(0, 2);
    }
  }

  if (lang === 'hi' || lang === 'mr') {
    switch (planetName) {
      case 'Sun': return 'सू';
      case 'Moon': return 'चं';
      case 'Mars': return 'मं';
      case 'Mercury': return 'बु';
      case 'Jupiter': return 'गु';
      case 'Venus': return 'शु';
      case 'Saturn': return 'श';
      case 'Rahu': return 'रा';
      case 'Ketu': return 'के';
      case 'Ascendant':
      case 'Lagna':
      case 'ASC': return 'ल';
      default: return planetName.slice(0, 2);
    }
  }

  // English: First letter Capitalized (Su, Mo, Ma, Me, Ju, Ve, Sa, Ra, Ke, Asc)
  switch (planetName) {
    case 'Sun': return 'Su';
    case 'Moon': return 'Mo';
    case 'Mars': return 'Ma';
    case 'Mercury': return 'Me';
    case 'Jupiter': return 'Ju';
    case 'Venus': return 'Ve';
    case 'Saturn': return 'Sa';
    case 'Rahu': return 'Ra';
    case 'Ketu': return 'Ke';
    case 'Ascendant':
    case 'Lagna':
    case 'ASC': return 'Asc';
    default: return planetName.charAt(0).toUpperCase() + planetName.slice(1, 3).toLowerCase();
  }
}

// Full Planet Names in English, Sanskrit, Punjabi, and Hindi
export function getPlanetFullName(planetName: PlanetName | string, lang: Language = 'en'): string {
  if (lang === 'sa') {
    return PLANETS_SA[planetName] || planetName;
  }
  if (lang === 'pa') {
    return PLANETS_PA[planetName] || planetName;
  }
  if (lang === 'hi') {
    switch (planetName) {
      case 'Sun': return 'सूर्य';
      case 'Moon': return 'चंद्र';
      case 'Mars': return 'मंगल';
      case 'Mercury': return 'बुध';
      case 'Jupiter': return 'गुरु';
      case 'Venus': return 'शुक्र';
      case 'Saturn': return 'शनि';
      case 'Rahu': return 'राहु';
      case 'Ketu': return 'केतु';
      case 'Ascendant': return 'लग्न';
      default: return planetName;
    }
  }
  return planetName;
}

// Zodiac Sign names in Hindi, Sanskrit, Punjabi
export const ZODIAC_HI: Record<ZodiacSign, string> = {
  Aries: 'मेष',
  Taurus: 'वृषभ',
  Gemini: 'मिथुन',
  Cancer: 'कर्क',
  Leo: 'सिंह',
  Virgo: 'कन्या',
  Libra: 'तुला',
  Scorpio: 'वृश्चिक',
  Sagittarius: 'धनु',
  Capricorn: 'मकर',
  Aquarius: 'कुंभ',
  Pisces: 'मीन'
};

export function getZodiacName(sign: ZodiacSign | string, lang: Language = 'en'): string {
  if (lang === 'sa' && ZODIAC_SA[sign as ZodiacSign]) {
    return ZODIAC_SA[sign as ZodiacSign];
  }
  if (lang === 'pa' && ZODIAC_PA[sign as ZodiacSign]) {
    return ZODIAC_PA[sign as ZodiacSign];
  }
  if (lang === 'hi' && ZODIAC_HI[sign as ZodiacSign]) {
    return ZODIAC_HI[sign as ZodiacSign];
  }
  return sign;
}

// 27 Nakshatras Hindi Mapping
export const NAKSHATRA_HI: Record<string, string> = {
  'Ashwini': 'अश्विनी',
  'Bharani': 'भरणी',
  'Krittika': 'कृत्तिका',
  'Rohini': 'रोहिणी',
  'Mrigashira': 'मृगशिरा',
  'Ardra': 'आर्द्रा',
  'Punarvasu': 'पुनर्वसु',
  'Pushya': 'पुष्य',
  'Ashlesha': 'अश्लेषा',
  'Magha': 'मघा',
  'Purva Phalguni': 'पूर्वाफाल्गुनी',
  'Uttara Phalguni': 'उत्तराफाल्गुनी',
  'Hasta': 'हस्त',
  'Chitra': 'चित्रा',
  'Swati': 'स्वाती',
  'Vishakha': 'विशाखा',
  'Anuradha': 'अनुराधा',
  'Jyeshta': 'ज्येष्ठा',
  'Mula': 'मूल',
  'Purva Ashadha': 'पूर्वाषाढा',
  'Uttara Ashadha': 'उत्तराषाढा',
  'Shravana': 'श्रवण',
  'Dhanishta': 'धनिष्ठा',
  'Shatabhisha': 'शतभिषा',
  'Purva Bhadrapada': 'पूर्वाभाद्रपद',
  'Uttara Bhadrapada': 'उत्तराभाद्रपद',
  'Revati': 'रेवती'
};

export function getNakshatraName(nakshatra: string, lang: Language = 'en'): string {
  if (lang === 'hi' && NAKSHATRA_HI[nakshatra]) {
    return NAKSHATRA_HI[nakshatra];
  }
  return nakshatra;
}

export function getGenderName(gender: string, lang: Language = 'en'): string {
  if (lang === 'hi') {
    const g = gender.toLowerCase();
    if (g === 'male') return 'पुरुष';
    if (g === 'female') return 'महिला';
    return 'अन्य';
  }
  return gender.charAt(0).toUpperCase() + gender.slice(1);
}

export const DIGNITY_HI: Record<string, string> = {
  'Exalted': 'उच्च',
  'Debilitated': 'नीच',
  'Own House': 'स्वराशि',
  'Moolatrikona': 'मूलत्रिकोण',
  'Great Friend': 'अति मित्र',
  'Friend': 'मित्र',
  'Neutral': 'सम',
  'Enemy': 'शत्रु',
  'Great Enemy': 'अति शत्रु',
  'Exalted (↑)': 'उच्च (↑)',
  'Debilitated (↓)': 'नीच (↓)'
};

export function getDignityName(dignity?: string | null, lang: Language = 'en'): string {
  if (!dignity) return '';
  if (lang === 'hi') {
    if (DIGNITY_HI[dignity]) return DIGNITY_HI[dignity];
    let translated = dignity;
    Object.entries(DIGNITY_HI).forEach(([en, hi]) => {
      translated = translated.replace(new RegExp(en, 'g'), hi);
    });
    return translated;
  }
  return dignity;
}

export const BENEFICENCE_HI: Record<string, string> = {
  'Benefic': 'शुभ',
  'Malefic': 'अशुभ',
  'Functional Benefic': 'कारक (शुभ)',
  'Functional Malefic': 'अकारक (अशुभ)',
  'Yogakaraka': 'योगकारक',
  'Supreme Yogakaraka': 'परम योगकारक',
  'Neutral': 'सम / तटस्थ',
  'Benefic Drishti': 'शुभ दृष्टि',
  'Malefic Drishti': 'अशुभ दृष्टि',
  'Natural Benefic': 'नैसर्गिक शुभ',
  'Natural Malefic': 'नैसर्गिक अशुभ',
  'Benefic (5+)': 'शुभ (5+)',
  'Combust': 'अस्त',
  'Retrograde': 'वक्री',
  'Direct': 'मार्गी',
  'Benefic Transit': 'शुभ गोचर',
  'Challenging Transit': 'कठिन गोचर'
};

export function getBeneficenceName(term?: string | null, lang: Language = 'en'): string {
  if (!term) return '';
  if (lang === 'hi') {
    if (BENEFICENCE_HI[term]) return BENEFICENCE_HI[term];
    let result = term;
    result = result.replace(/Benefic Drishti/gi, 'शुभ दृष्टि')
                   .replace(/Malefic Drishti/gi, 'अशुभ दृष्टि')
                   .replace(/Functional Benefic/gi, 'कारक (शुभ)')
                   .replace(/Functional Malefic/gi, 'अकारक (अशुभ)')
                   .replace(/Supreme Yogakaraka/gi, 'परम योगकारक')
                   .replace(/Yogakaraka/gi, 'योगकारक')
                   .replace(/Natural Benefic/gi, 'नैसर्गिक शुभ')
                   .replace(/Natural Malefic/gi, 'नैसर्गिक अशुभ')
                   .replace(/Benefic/gi, 'शुभ')
                   .replace(/Malefic/gi, 'अशुभ')
                   .replace(/Neutral/gi, 'सम');
    return result;
  }
  return term;
}

export const AVASTHA_HI: Record<string, string> = {
  'Bala (Infant)': 'बाल अवस्था (शिशु)',
  'Kumara (Youth)': 'कुमार अवस्था (युवा/किशोरी)',
  'Yuva (Adult)': 'युवा अवस्था (प्रौढ़/पूर्ण)',
  'Vriddha (Old)': 'वृद्ध अवस्था (अनुभवी/शांत)',
  'Mrita (Deceased)': 'मृत अवस्था (सुप्त/अक्रिय)'
};

export function getAvasthaName(avastha?: string | null, lang: Language = 'en'): string {
  if (!avastha) return '';
  if (lang === 'hi') {
    if (AVASTHA_HI[avastha]) return AVASTHA_HI[avastha];
    return avastha.replace('Bala (Infant)', 'बाल अवस्था')
                  .replace('Kumara (Youth)', 'कुमार अवस्था')
                  .replace('Yuva (Adult)', 'युवा अवस्था')
                  .replace('Vriddha (Old)', 'वृद्ध अवस्था')
                  .replace('Mrita (Deceased)', 'मृत अवस्था');
  }
  return avastha;
}

export function getAvasthaEffect(avastha: string, lang: Language = 'en', percentage: number = 100): string {
  if (lang === 'hi') {
    if (avastha.includes('Bala')) return `उभरता हुआ फल (लगभग ${percentage}% सामर्थ्य)`;
    if (avastha.includes('Kumara')) return `सक्रिय एवं विकासशील फल (लगभग ${percentage}% सामर्थ्य)`;
    if (avastha.includes('Yuva')) return `पूर्ण एवं सशक्त परिणाम (लगभग ${percentage}% सामर्थ्य)`;
    if (avastha.includes('Vriddha')) return `धीमा व अनुभव-आधारित फल (लगभग ${percentage}% सामर्थ्य)`;
    if (avastha.includes('Mrita')) return `सुप्त फल (राशि स्वामी पर निर्भर, ${percentage}% सीधा प्रभाव)`;
  }
  if (avastha.includes('Bala')) return `Mild, emerging planetary power (${percentage}% manifestation).`;
  if (avastha.includes('Kumara')) return `Active, developing results (${percentage}% manifestation).`;
  if (avastha.includes('Yuva')) return `Full, prime planetary strength (${percentage}% manifestation).`;
  if (avastha.includes('Vriddha')) return `Declining planetary vigor; passive effects (${percentage}%).`;
  return `Dormant planetary strength; relies on dispositor (${percentage}%).`;
}

export function translateRoleDescription(desc?: string | null, lang: Language = 'en'): string {
  if (!desc) return '';
  if (lang === 'hi') {
    let t = desc;
    t = t.replace(/Supreme Yogakaraka \(Lord of (.*)\)/gi, 'परम योगकारक ($1 स्वामी)')
         .replace(/Functional Benefic \(Lord of (.*)\)/gi, 'कारक शुभ ग्रह ($1 स्वामी)')
         .replace(/Functional Malefic \(Lord of (.*)\)/gi, 'अकारक अशुभ ग्रह ($1 स्वामी)')
         .replace(/Supreme Yogakaraka/gi, 'परम योगकारक')
         .replace(/Functional Benefic/gi, 'कारक (शुभ)')
         .replace(/Functional Malefic/gi, 'अकारक (अशुभ)')
         .replace(/Benefic/gi, 'शुभ')
         .replace(/Malefic/gi, 'अशुभ')
         .replace(/Neutral/gi, 'सम / तटस्थ')
         .replace(/5th Trikona & 10th Kendra/gi, 'पंचम त्रिकोण एवं दशम केंद्र')
         .replace(/4th Kendra & 9th Trikona/gi, 'चतुर्थ केंद्र एवं नवम त्रिकोण')
         .replace(/4th Kendra & 5th Trikona/gi, 'चतुर्थ केंद्र एवं पंचम त्रिकोण')
         .replace(/9th Dharma/gi, 'नवम धर्म भाव')
         .replace(/Lagna/gi, 'लग्न')
         .replace(/house/gi, 'भाव')
         .replace(/Lord of/gi, 'स्वामी:');
    return t;
  }
  return desc;
}

// House Categories Translation
export const HOUSE_CATEGORY_HI: Record<string, string> = {
  'Kendra (Angular)': 'केन्द्र',
  'Trikona (Trine)': 'त्रिकोण',
  'Dharma': 'धर्म',
  'Artha (Material Wealth)': 'अर्थ',
  'Artha': 'अर्थ',
  'Kama (Desire)': 'काम',
  'Kama': 'काम',
  'Moksha': 'मोक्ष',
  'Dusthana (Difficult)': 'दुःस्थान',
  'Upachaya (Growth)': 'उपचय',
  'Upachaya': 'उपचय',
  'Maraka (Killer / Transition)': 'मारक',
  'Maraka': 'मारक',
  'Trishadaaya': 'त्रिषडाय',
  'Trik (Occult)': 'त्रिक (गूढ़)'
};

export function getHouseCategoryName(cat: string, lang: Language = 'en'): string {
  if (lang === 'hi' && HOUSE_CATEGORY_HI[cat]) {
    return HOUSE_CATEGORY_HI[cat];
  }
  return cat;
}

// House Signification Details in Hindi
export const HOUSE_DETAILS_HI: Record<number, {
  sanskritName: string;
  title: string;
  description: string;
  themes: string[];
}> = {
  1: {
    sanskritName: 'तनु भाव (लग्न)',
    title: 'आत्म, जीवन शक्ति एवं व्यक्तित्व भाव',
    description: 'संपूर्ण कुंडली की आधारशिला जो आपकी आत्म-अभिव्यक्ति, शारीरिक स्वास्थ्य, जीवन शक्ति और जीवन के प्रति प्राथमिक दृष्टिकोण का प्रतिनिधित्व करती है।',
    themes: ['शारीरिक स्वरूप', 'रंग-रूप', 'स्वास्थ्य व जीवन शक्ति', 'व्यक्तित्व', 'जीवन पथ', 'आत्म प्रतिष्ठा']
  },
  2: {
    sanskritName: 'धन एवं कुटुंब भाव',
    title: 'धन, परिवार एवं वाणी भाव',
    description: 'संचित धन, बचत, बैंक संपत्ति, पारिवारिक मूल्य, वाणी की सत्यता और भोजन संबंधी आदतों को नियंत्रित करता है।',
    themes: ['संचित धन', 'कुल परंपरा', 'वाणी व स्वर', 'दाहिनी आंख', 'खान-पान', 'प्रारंभिक संस्कार']
  },
  3: {
    sanskritName: 'सहज एवं भ्रातृ भाव',
    title: 'साहस, भाई-बहन एवं कौशल भाव',
    description: 'व्यक्तिगत पराक्रम, बाहुबल व कला-कौशल, दृढ़ निश्चय, लेखन प्रतिभा और छोटे भाई-बहनों के साथ संबंधों को दर्शाता है।',
    themes: ['छोटे भाई-बहन', 'साहस व पराक्रम', 'हस्तशिल्प व कला', 'लेखन व मीडिया', 'छोटी यात्राएं', 'पहल व उद्यम']
  },
  4: {
    sanskritName: 'सुख एवं मातृ भाव',
    title: 'गृह, माता एवं मानसिक शांति भाव',
    description: 'भावनात्मक सुख का आंतरिक धाम, मातृ संबंध, गृह शांति, अचल संपत्ति और वाहन सुख का प्रतिनिधित्व करता है।',
    themes: ['माता', 'आंतरिक सुख (संतोष)', 'भूमि व भवन', 'वाहन सुख', 'औपचारिक शिक्षा', 'पारिवारिक जड़े']
  },
  5: {
    sanskritName: 'पुत्र एवं पूर्व पुण्य भाव',
    title: 'बुद्धि, संतान एवं पूर्व पुण्य भाव',
    description: 'रचनात्मक प्रतिभा, अंतर्ज्ञान, उच्च विद्या, मंत्र सिद्धि और पूर्व जन्म के शुभ कर्मों का सर्वोच्च भाव है।',
    themes: ['संतान व संतति', 'पूर्व जन्म पुण्य', 'बुद्धि व विवेक (धी)', 'प्रेम व रोमांस', 'मंत्र साधना व विद्या']
  },
  6: {
    sanskritName: 'अरि एवं ऋण भाव',
    title: 'ऋण, रोग एवं शत्रु विजय भाव',
    description: 'कठिन परिश्रम करने की क्षमता, विवादों का समाधान, शत्रु विजय, ऋण प्रबंधन और शारीरिक रोग प्रतिरोधक क्षमता को दर्शाता है।',
    themes: ['प्रतियोगी विजय', 'रोग निवारण व स्वास्थ्य', 'ऋण व कर्ज', 'दैनिक कार्य', 'सेवा भावना', 'मुकदमेबाजी']
  },
  7: {
    sanskritName: 'कलत्र एवं जाया भाव',
    title: 'विवाह, साझेदारी एवं जनसंपर्क भाव',
    description: 'दीर्घकालिक वैवाहिक संबंध, जीवनसाथी के गुण, कानूनी अनुबंध, व्यावसायिक साझेदारी और सामाजिक प्रभाव को दर्शाता है।',
    themes: ['जीवनसाथी व विवाह', 'व्यापारिक साझेदारी', 'सार्वजनिक संबंध', 'विदेश प्रवास', 'अनुबंध व व्यापार', 'सामाजिक कूटनीति']
  },
  8: {
    sanskritName: 'आयु एवं रंध्र भाव',
    title: 'आयु, रहस्य एवं परिवर्तन भाव',
    description: 'गूढ़ आध्यात्मिक ज्ञान, मानसिक गहराई, दीर्घायु, पैतृक संपत्ति और जीवन में बड़े परिवर्तनकारी पुनर्जन्म का रहस्यमय क्षेत्र है।',
    themes: ['दीर्घायु व जीवन काल', 'गूढ़ व ज्योतिष विद्या', 'पैतृक धन व वसीयत', 'आकस्मिक परिवर्तन', 'गहन शोध व अनुसंधान']
  },
  9: {
    sanskritName: 'भाग्य एवं धर्म भाव',
    title: 'भाग्य, उच्च ज्ञान एवं धर्म भाव',
    description: 'भाग्य, दैवीय कृपा, नैतिक आचरण, उच्च दर्शन व तीर्थयात्रा, तथा पिता व गुरुओं के आशीर्वाद का परम शुभ भाव है।',
    themes: ['दैवीय भाग्य', 'पिता व गुरु कृपा', 'उच्च दर्शन व धर्म', 'तीर्थयात्राएं व लंबी यात्रा', 'सदाचार व नैतिकता']
  },
  10: {
    sanskritName: 'कर्म एवं राज्य भाव',
    title: 'करियर, प्रतिष्ठा एवं सामाजिक पद भाव',
    description: 'आकाश का सर्वोच्च बिंदु जो व्यावसायिक सफलता, करियर उपलब्धियों, सामाजिक अधिकार, मान-सम्मान और जन-प्रतिष्ठा को दर्शाता है।',
    themes: ['करियर व आजीविका', 'जन प्रतिष्ठा व सम्मान', 'शासन व नेतृत्व', 'कार्य सिद्धि', 'सामाजिक कर्तव्य']
  },
  11: {
    sanskritName: 'लाभ एवं आय भाव',
    title: 'लाभ, आय एवं मनोकामना पूर्ति भाव',
    description: 'निरंतर आय प्रवाह, जीवन की आकांक्षाओं की पूर्ति, करियर से लाभ, सामाजिक नेटवर्क और बड़े भाई-बहनों का प्रतिनिधित्व करता है।',
    themes: ['वित्तीय लाभ व आय', 'बड़े भाई-बहन', 'प्रभावशाली मित्र', 'मनोकामना पूर्ति', 'सामाजिक नेटवर्क', 'पुरस्कार व सम्मान']
  },
  12: {
    sanskritName: 'व्यय एवं मोक्ष भाव',
    title: 'व्यय, विदेश गमन एवं मोक्ष भाव',
    description: 'आध्यात्मिक मुक्ति (मोक्ष), त्याग, दूरस्थ विदेशी यात्रा या निवास, अवचेतन ध्यान और परोपकार का प्रवेश द्वार है।',
    themes: ['मोक्ष व मुक्ति', 'विदेश गमन व प्रवास', 'व्यय व निवेश', 'ध्यान व एकांत', 'अनिद्रा व स्वप्न', 'परोपकार व दान']
  }
};

export function getHouseSanskritName(houseNum: number, lang: Language = 'en', defaultVal: string = ''): string {
  if (lang === 'hi' && HOUSE_DETAILS_HI[houseNum]) {
    return HOUSE_DETAILS_HI[houseNum].sanskritName;
  }
  return defaultVal;
}

export function getHouseTitle(houseNum: number, lang: Language = 'en', defaultVal: string = ''): string {
  if (lang === 'hi' && HOUSE_DETAILS_HI[houseNum]) {
    return HOUSE_DETAILS_HI[houseNum].title;
  }
  return defaultVal;
}

export function getHouseDescription(houseNum: number, lang: Language = 'en', defaultVal: string = ''): string {
  if (lang === 'hi' && HOUSE_DETAILS_HI[houseNum]) {
    return HOUSE_DETAILS_HI[houseNum].description;
  }
  return defaultVal;
}

export function getHouseThemes(houseNum: number, lang: Language = 'en', defaultThemes: string[] = []): string[] {
  if (lang === 'hi' && HOUSE_DETAILS_HI[houseNum]) {
    return HOUSE_DETAILS_HI[houseNum].themes;
  }
  return defaultThemes;
}

// Verdict & Status Translations
export const VERDICT_BADGE_HI: Record<string, string> = {
  'Highly Auspicious & Powerful': 'अत्यंत शुभ एवं सशक्त',
  'Strong & Favorable': 'सशक्त एवं शुभ',
  'Balanced & Steady': 'संतुलित एवं सामान्य',
  'Karmic Growth / Demands Remedy': 'कर्मिक चुनौती / उपाय आवश्यक'
};

export function getVerdictBadgeName(badge: string, lang: Language = 'en'): string {
  if (lang === 'hi' && VERDICT_BADGE_HI[badge]) {
    return VERDICT_BADGE_HI[badge];
  }
  return badge;
}

export const CLIMATE_BADGE_HI: Record<string, string> = {
  'Supreme Auspicious': 'परम शुभ एवं फलदायी',
  'Favorable & Productive': 'शुभ एवं फलप्रद',
  'Dynamic & Mixed': 'सक्रिय एवं मिश्रित',
  'Vigilant / Transformative': 'सतर्कता / परिवर्तनकारी'
};

export function getClimateBadgeName(badge: string, lang: Language = 'en'): string {
  if (lang === 'hi' && CLIMATE_BADGE_HI[badge]) {
    return CLIMATE_BADGE_HI[badge];
  }
  return badge;
}

export const AV_RATING_HI: Record<string, string> = {
  'Exceptional (32+)': 'अति उत्तम (32+ बिंदु)',
  'Strong & Auspicious (28-31)': 'सशक्त एवं शुभ (28-31 बिंदु)',
  'Moderate (25-27)': 'मध्यम (25-27 बिंदु)',
  'Challenging (<25)': 'संवेदनशील (<25 बिंदु)'
};

export function getAvRatingName(rating: string, lang: Language = 'en'): string {
  if (lang === 'hi' && AV_RATING_HI[rating]) {
    return AV_RATING_HI[rating];
  }
  return rating;
}

export function translateHouseStrengthSummary(summary: string, houseNum: number, lang: Language = 'en'): string {
  if (lang === 'hi') {
    if (summary.includes('exceptional astrological backing')) {
      return `भाव ${houseNum} को उच्च या शुभ ग्रहों के प्रभाव तथा उत्तम प्राण शक्ति से विशेष बल प्राप्त हो रहा है।`;
    }
    if (summary.includes('positively fortified with supportive lord placement')) {
      return `भाव ${houseNum} भावेश की शुभ स्थिति और अनुकूल ग्रहीय दृष्टियों से भली-भांति सुरक्षित व सक्रिय है।`;
    }
    if (summary.includes('balanced mix of cosmic influences')) {
      return `भाव ${houseNum} पर संतुलित ग्रहीय प्रभाव हैं; निरंतर प्रयासों से समय के साथ स्थिर परिणाम मिलते हैं।`;
    }
    if (summary.includes('encounters affliction or debilitation')) {
      return `भाव ${houseNum} में नीच या पाप प्रभाव की स्थिति है; यह चुनौतियां आत्म-विकास और वैदिक उपायों द्वारा हल की जा सकती हैं।`;
    }
  }
  return summary;
}

export function translateAvAnalysis(analysis: string, houseNum: number, savPoints: number, lang: Language = 'en'): string {
  if (lang === 'hi') {
    if (savPoints >= 32) {
      return `${savPoints} सर्वाष्टकवर्ग बिंदुओं (28 के मानक से कहीं अधिक) के साथ इस भाव में सर्वोच्च सामर्थ्य है। इस भाव से जुड़े कार्य सहजता से पूर्ण होते हैं तथा किसी भी बाधा से शीघ्र उबरने की क्षमता रहती है।`;
    }
    if (savPoints >= 28) {
      return `${savPoints} बिंदुओं के साथ यह भाव 28 के शास्त्रीय मानक से ऊपर है। यह इस भाव से संबंधित सभी कार्यों के लिए एक मजबूत और फलदायी आधार प्रदान करता है।`;
    }
    if (savPoints >= 25) {
      return `${savPoints} बिंदुओं (28 के औसत के निकट) के साथ यह भाव मध्यम सामर्थ्य रखता है। इसके परिणाम पूर्णतः व्यक्तिगत अनुशासन, परिश्रम और अनुकूल दशा पर निर्भर करते हैं।`;
    }
    return `${savPoints} बिंदुओं (25 से कम) के कारण इस भाव में अष्टकवर्ग बल कम है। इस भाव से जुड़े मामलों में बिना सावधानी के विलंब या रुकावटें आ सकती हैं।`;
  }
  return analysis;
}

export function translateAvTransit(transit: string, houseNum: number, savPoints: number, lang: Language = 'en'): string {
  if (lang === 'hi') {
    if (savPoints >= 32) {
      return `इस राशि पर गोचर करने वाले ग्रह—विशेष रूप से गुरु और शुक्र जैसे शुभ ग्रह—उत्कृष्ट फल, नए अवसर और बड़ी उपलब्धियां प्रदान करते हैं।`;
    }
    if (savPoints >= 28) {
      return `ग्रहों का गोचर इस भाव को अनुकूल रूप से सक्रिय करता है; यहाँ किया गया परिश्रम प्रत्यक्ष और ठोस सफलता में बदलता है।`;
    }
    if (savPoints >= 25) {
      return `इस राशि से शनि या राहु जैसे ग्रहों का गोचर धैर्य, विवेकपूर्ण योजना और यथार्थवादी अपेक्षाओं की मांग करता है।`;
    }
    return `इस राशि पर गोचर के समय अतिरिक्त सावधानी बरतें, भावेश को बल प्रदान करें तथा पाप प्रभाव की अवधि में जोखिम भरे निर्णयों से बचें।`;
  }
  return transit;
}

export function translateGocharVerdict(verdict: string, houseNum: number, lang: Language = 'en'): string {
  if (lang === 'hi') {
    if (verdict.includes('auspicious Jupiter transit blessings')) {
      return `भाव ${houseNum} को गुरु के शुभ गोचर का आशीर्वाद और अष्टकवर्ग का उत्तम बल प्राप्त हो रहा है। इस भाव से जुड़े कार्य सुगमता से आगे बढ़ेंगे।`;
    }
    if (verdict.includes("Saturn's active transit")) {
      return `भाव ${houseNum} में शनि का गोचर धैर्य, अनुशासित कार्यशैली और जल्दबाजी से बचने की मांग करता है।`;
    }
    if (verdict.includes("Rahu's presence")) {
      return `भाव ${houseNum} में राहु की उपस्थिति महत्वाकांक्षा और नए रास्ते खोलती है, परंतु भ्रम और जल्दबाजी से सावधान रहना आवश्यक है।`;
    }
    if (verdict.includes('Active planetary presence')) {
      return `ग्रहों की सक्रिय उपस्थिति भाव ${houseNum} को विशेष गतिशीलता प्रदान कर रही है, जिससे इसके मुख्य विषयों में ऊर्जा और सक्रियता बढ़ रही है।`;
    }
    if (verdict.includes('receiving dynamic transit aspects')) {
      return `भाव ${houseNum} पर वर्तमान में शुभ गोचर दृष्टियां पड़ रही हैं, जो इसे पृष्ठभूमि में निरंतर सकारात्मक सहारा दे रही हैं।`;
    }
    if (verdict.includes('serene transit window')) {
      return `भाव ${houseNum} पर वर्तमान में कोई गंभीर पाप प्रभाव नहीं है, यह स्थिर प्रगति और योजनाओं को सुचारू रूप से आगे बढ़ाने का समय है।`;
    }
  }
  return verdict;
}

export function translateTimingGuidance(guidance: string, lang: Language = 'en'): string {
  if (lang === 'hi') {
    if (guidance.includes('Optimal time to initiate major ventures')) {
      return `नई योजनाओं को आरंभ करने, महत्वपूर्ण समझौतों पर हस्ताक्षर करने और अवसरों का पूरा लाभ उठाने का यह सर्वोत्तम समय है।`;
    }
    if (guidance.includes('Adopt structured routines')) {
      return `अनुशासित दिनचर्या अपनाएं, कानूनी व वित्तीय दस्तावेजों की जांच करें और वरिष्ठों व परिजनों के साथ धैर्य बनाए रखें।`;
    }
    if (guidance.includes('Harness innovative ideas')) {
      return `रचनात्मक व नए विचारों का स्वागत करें परंतु तथ्यों की पुष्टि अवश्य करें; सट्टेबाजी या जल्दबाजी में निर्णय न लें।`;
    }
    if (guidance.includes('Stay engaged with proactive initiatives')) {
      return `सक्रिय पहलों में लगे रहें, निरंतर प्रयास बनाए रखें और ग्रहीय गतिशीलता का पूरा लाभ उठाएं।`;
    }
    if (guidance.includes('Align with the harmonious qualities')) {
      return `शुभ दृष्टियों के सकारात्मक गुणों को अपनाएं और दैनिक जीवन में अनुशासन व संतुलन बनाए रखें।`;
    }
    if (guidance.includes('Favorable period for steady progress')) {
      return `स्थिर प्रगति करने, लाभ को मजबूत करने और दीर्घकालिक योजनाओं पर निरंतर कार्य करने का अनुकूल समय है।`;
    }
  }
  return guidance;
}

export function translateVedicImpact(impact: string, planetName: string, lang: Language = 'en'): string {
  if (lang === 'hi') {
    if (planetName === 'Jupiter') {
      return `गुरु का गोचर इस भाव को सीधे सक्रिय कर रहा है। ज्ञान, शुभता, ईश्वरीय कृपा और भौतिक सुखों में वृद्धि करता है।`;
    }
    if (planetName === 'Saturn') {
      if (impact.includes('Sade Sati')) {
        return `शनि का गोचर यहाँ साढ़ेसाती के अंतर्गत है। अनुशासित परिश्रम, धैर्य और परिपक्व व्यवहार की आवश्यकता है।`;
      }
      if (impact.includes('Dhaiya') || impact.includes('Kantaka')) {
        return `शनि का गोचर ढैय्या / कंटक प्रभाव में है। एकाग्रता बनाए रखें और विलंब होने पर धैर्य रखें।`;
      }
      return `शनि का गोचर जीवन में अनुशासन, स्थिरता, दृढ़ संकल्प और कर्म शुद्धि प्रदान करता है।`;
    }
    if (planetName === 'Rahu') {
      return `राहु का गोचर तीव्र महत्वाकांक्षा, अचानक सफलता की इच्छा, विदेशी संपर्क और नए अनूठे रास्ते खोलता है।`;
    }
    if (planetName === 'Ketu') {
      return `केतु का गोचर अंतर्ज्ञान, आध्यात्मिक वैराग्य, विश्लेषणात्मक दृष्टि और कर्म बंधनों से मुक्ति लाता है।`;
    }
    if (planetName === 'Sun') {
      return `सूर्य का गोचर इस भाव के मामलों को आत्मबल, नेतृत्व, प्रतिष्ठा और ऊर्जा से प्रकाशित करता है।`;
    }
    if (planetName === 'Mars') {
      return `मंगल का गोचर उच्च ऊर्जा, शारीरिक तत्परता, साहस और त्वरित निर्णय लेने की क्षमता प्रदान करता है।`;
    }
    if (planetName === 'Venus') {
      return `शुक्र का गोचर सौंदर्य, रचनात्मकता, आपसी संबंधों में मधुरता और सुख-सुविधाओं में वृद्धि करता है।`;
    }
    if (planetName === 'Mercury') {
      return `बुध का गोचर बुद्धि, व्यापारिक कौशल, त्वरित विचार और संचार क्षमता को तीव्र करता है।`;
    }
    if (planetName === 'Moon') {
      return `चंद्रमा का गोचर अल्पकालिक मानसिक तरंगे, संवेदनशीलता और अंतःप्रेरणा को जाग्रत करता है।`;
    }
  }
  return impact;
}

export function translateAspectImpact(impact: string, planetName: string, lang: Language = 'en'): string {
  if (lang === 'hi') {
    if (planetName === 'Jupiter') {
      return `गोचरस्थ गुरु अपनी शुभ दृष्टि डालकर भाव की रक्षा, ज्ञान और नकारात्मक प्रभावों का शमन करते हैं।`;
    }
    if (planetName === 'Saturn') {
      return `गोचरस्थ शनि अपनी दृष्टि से अनुशासन, यथार्थवादी दृष्टिकोण और धैर्य की परीक्षा लेते हैं।`;
    }
    if (planetName === 'Mars') {
      return `गोचरस्थ मंगल अपनी दृष्टि से भाव में साहस, तत्परता और ऊर्जा का संचार करते हैं।`;
    }
    if (planetName === 'Rahu' || planetName === 'Ketu') {
      return `गोचरस्थ ${getPlanetFullName(planetName, 'hi')} की दृष्टि अप्रत्याशित और अनूठे घटनाक्रम को प्रेरित करती है।`;
    }
    return `गोचरस्थ ${getPlanetFullName(planetName, 'hi')} की दृष्टि इस भाव के मामलों को सक्रिय करती है।`;
  }
  return impact;
}

export function translateAshtakvargaSynergy(synergy: string, savPoints: number, lang: Language = 'en'): string {
  if (lang === 'hi') {
    if (savPoints >= 30) {
      return `उच्च सर्वाष्टकवर्ग स्कोर (${savPoints} बिंदु) एक मजबूत कर्मिक आधार प्रदान करता है। गोचरस्थ ग्रह न्यूनतम बाधा के साथ अपना सर्वोत्तम शुभ फल देते हैं।`;
    }
    if (savPoints >= 26) {
      return `संतुलित सर्वाष्टकवर्ग स्कोर (${savPoints} बिंदु)। ग्रहों का गोचर जागरूक प्रयास और योजनाबद्ध कार्य के अनुपात में सकारात्मक फल देता है।`;
    }
    return `संवेदनशील सर्वाष्टकवर्ग स्कोर (${savPoints} बिंदु)। गोचर के उत्तम फल के लिए अधिक सजगता, धैर्य और वैदिक उपायों की आवश्यकता होती है।`;
  }
  return synergy;
}

export const PAKSHA_HI: Record<string, string> = {
  'Shukla Paksha': 'शुक्ल पक्ष',
  'Krishna Paksha': 'कृष्ण पक्ष'
};

export function getPakshaName(paksha: string, lang: Language = 'en'): string {
  if (lang === 'hi' && PAKSHA_HI[paksha]) {
    return PAKSHA_HI[paksha];
  }
  return paksha;
}

export const VARA_HI: Record<string, string> = {
  'Sunday': 'रविवार',
  'Monday': 'सोमवार',
  'Tuesday': 'मंगलवार',
  'Wednesday': 'बुधवार',
  'Thursday': 'गुरुवार',
  'Friday': 'शुक्रवार',
  'Saturday': 'शनिवार'
};

export function getVaraName(vara: string, lang: Language = 'en'): string {
  if (lang === 'hi' && VARA_HI[vara]) {
    return VARA_HI[vara];
  }
  return vara;
}

export function getTithiName(tithi: string, lang: Language = 'en'): string {
  if (lang === 'hi') {
    let result = tithi;
    result = result.replace('Shukla', 'शुक्ल').replace('Krishna', 'कृष्ण');
    result = result.replace('Pratipada', 'प्रतिपदा').replace('Dwitiya', 'द्वितीया').replace('Tritiya', 'तृतीया')
      .replace('Chaturthi', 'चतुर्थी').replace('Panchami', 'पंचमी').replace('Shashthi', 'षष्ठी')
      .replace('Saptami', 'सप्तमी').replace('Ashtami', 'अष्टमी').replace('Navami', 'नवमी')
      .replace('Dashami', 'दशमी').replace('Ekadashi', 'एकादशी').replace('Dwadashi', 'द्वादशी')
      .replace('Trayodashi', 'त्रयोदशी').replace('Chaturdashi', 'चतुर्दशी').replace('Purnima', 'पूर्णिमा')
      .replace('Amavasya', 'अमावस्या');
    return result;
  }
  return tithi;
}

export const AVAKHADA_HI: Record<string, Record<string, string>> = {
  varna: {
    'Brahmin': 'ब्राह्मण',
    'Kshatriya': 'क्षत्रिय',
    'Vaishya': 'वैश्य',
    'Shudra': 'शूद्र'
  },
  gana: {
    'Deva': 'देव',
    'Manushya': 'मनुष्य',
    'Rakshasa': 'राक्षस'
  },
  nadi: {
    'Adi': 'आदि (प्रथम)',
    'Madhya': 'मध्य',
    'Antya': 'अंत्य (अंतिम)'
  },
  yoni: {
    'Horse': 'अश्व (घोड़ा)',
    'Elephant': 'गज (हाथी)',
    'Sheep': 'मेष (भेड़)',
    'Serpent': 'सर्प (सांप)',
    'Dog': 'श्वान (कुत्ता)',
    'Cat': 'मार्जार (बिल्ली)',
    'Rat': 'मूषक (चूहा)',
    'Cow': 'गौ (गाय)',
    'Buffalo': 'महिष (भैंस)',
    'Tiger': 'व्याघ्र (बाघ)',
    'Hare': 'शशक (खरगोश)',
    'Monkey': 'वानर (बंदर)',
    'Mongoose': 'नकुल (नेवला)',
    'Lion': 'सिंह (शेर)'
  },
  tatva: {
    'Agni': 'अग्नि (Fire)',
    'Prithvi': 'पृथ्वी (Earth)',
    'Vayu': 'वायु (Air)',
    'Jala': 'जल (Water)',
    'Akasha': 'आकाश (Ether)'
  },
  paya: {
    'Gold': 'स्वर्ण (सोना)',
    'Silver': 'रजत (चांदी)',
    'Copper': 'ताम्र (तांबा)',
    'Iron': 'लौह (लोहा)'
  },
  vasya: {
    'Chatushpada': 'चतुष्पद (चौपाया)',
    'Dwipada': 'द्विपद (मानव)',
    'Jalachara': 'जलचर',
    'Vanachara': 'वनचर',
    'Keeta': 'कीट (वृश्चिक आदि)'
  }
};

export function getAvakhadaValue(category: string, value: string, lang: Language = 'en'): string {
  if (lang === 'hi' && value) {
    const cat = category.toLowerCase();
    if (AVAKHADA_HI[cat] && AVAKHADA_HI[cat][value]) {
      return AVAKHADA_HI[cat][value];
    }
    // Partial replacements
    for (const [k, v] of Object.entries(AVAKHADA_HI)) {
      if (v[value]) return v[value];
    }
  }
  return value;
}

export const YOGA_HI: Record<string, string> = {
  'Vishkumbha': 'विष्कुम्भ',
  'Priti': 'प्रीति',
  'Ayushman': 'आयुष्मान',
  'Saubhagya': 'सौभाग्य',
  'Shobhana': 'शोभन',
  'Atiganda': 'अतिगण्ड',
  'Sukarma': 'सुकर्मा',
  'Dhriti': 'धृति',
  'Shula': 'शूल',
  'Ganda': 'गण्ड',
  'Vriddhi': 'वृद्धि',
  'Dhruva': 'ध्रुव',
  'Vyaghata': 'व्याघात',
  'Harshana': 'हर्षण',
  'Vajra': 'वज्र',
  'Siddhi': 'सिद्धि',
  'Vyatipata': 'व्यतीपात',
  'Variyan': 'वरीयान्',
  'Parigha': 'परिघ',
  'Shiva': 'शिव',
  'Siddha': 'सिद्ध',
  'Sadhya': 'साध्य',
  'Shubha': 'शुभ',
  'Shukla': 'शुक्ल',
  'Brahma': 'ब्रह्म',
  'Indra': 'इन्द्र',
  'Vaidhriti': 'वैधृति'
};

export function getPanchangYogaName(yoga: string, lang: Language = 'en'): string {
  if (lang === 'hi' && YOGA_HI[yoga]) {
    return YOGA_HI[yoga];
  }
  return yoga;
}

export const KARANA_HI: Record<string, string> = {
  'Bava': 'बव',
  'Balava': 'बालव',
  'Kaulava': 'कौलव',
  'Taitila': 'तैतिल',
  'Gara': 'गर',
  'Vanija': 'वणिज',
  'Vishti': 'विष्टि (भद्रा)',
  'Shakuni': 'शकुनि',
  'Chatushpada': 'चतुष्पद',
  'Naga': 'नाग',
  'Kimstughna': 'किंस्तुघ्न'
};

export function getKaranaName(karana: string, lang: Language = 'en'): string {
  if (lang === 'hi' && KARANA_HI[karana]) {
    return KARANA_HI[karana];
  }
  return karana;
}

// UI Text Dictionary for Header and Controls
export const UI_TEXT = {
  en: {
    appTitle: 'Vedanga Jyotisha',
    appSubtitle: 'Celestial Science',
    chartsTab: 'Charts',
    planetsTab: 'Planets & Panchang',
    gocharTab: 'Current Gochar (Transits)',
    shadbalaTab: 'Shadbala & Strengths',
    dashasTab: 'Vimshottari Dashas',
    doshasTab: 'Doshas & Yogas',
    ashtakvargaTab: 'Ashtakvarga',
    varshphalTab: 'Varshphal (Annual)',
    prashnaTab: 'Prashna (Horary)',
    remediesTab: 'Gemstones & Remedies',
    matchingTab: 'Gun Milan (Matching)',
    numerologyTab: 'Numerology & Loshu',
    palmistryTab: 'Palm Study (Samudrika)',
    vastuTab: 'Vastu & Home Directions',
    aiTab: 'AI Astrologer',
    northIndian: 'North Indian',
    southIndian: 'South Indian',
    eastIndian: 'East Indian',
    singleView: '1 Chart',
    trioView: '3 Chart',
    quadView: '4 Chart',
    language: 'Language',
    house: 'House',
    lord: 'Lord',
    retrograde: 'Retrograde',
    exalted: 'Exalted',
    debilitated: 'Debilitated',
    ownHouse: 'Own House',
    combust: 'Combust',
    rashi: 'Rashi',
    points: 'pt',
    showDegrees: 'Show Degrees',
    hideDegrees: 'Hide Degrees',
    lagna: 'Lagna',
    moonSign: 'Moon Sign',
    nakshatra: 'Nakshatra',
    famousProfiles: 'Famous Profiles',
    saved: 'Saved Kundali',
    createKundali: 'Create Kundali',
    printReport: 'Print Report',
    period: 'Period',
    // Beneficence
    benefic: 'Benefic',
    malefic: 'Malefic',
    neutral: 'Neutral',
    // Divisional Charts
    D1: 'Lagna Kundali',
    Chandra: 'Chandra Kundali',
    Surya: 'Surya Kundali',
    Transit: 'Gochar Kundali',
    D2: 'Hora Kundali',
    D3: 'Drekkana Kundali',
    D4: 'Chaturthamsha Kundali',
    D7: 'Saptamsha Kundali',
    D9: 'Navamsha Kundali',
    D10: 'Dashamsha Kundali',
    D12: 'Dwadashamsha Kundali',
    D16: 'Shodashamsha Kundali',
    D20: 'Vimshamsha Kundali',
    D24: 'Chaturvimshamsha Kundali',
    D27: 'Saptavimshamsha Kundali',
    D30: 'Trimshamsha Kundali',
    D40: 'Khavedamsha Kundali',
    D45: 'Akshavedamsha Kundali',
    D60: 'Shashtiamsha Kundali',
    // House meanings
    houseMeanings: {
      1: { name: 'Tanu Bhava', desc: 'Self, Physical Body & Life Path' },
      2: { name: 'Dhana Bhava', desc: 'Wealth, Family & Speech' },
      3: { name: 'Sahaja Bhava', desc: 'Courage, Siblings & Skills' },
      4: { name: 'Sukha Bhava', desc: 'Mother, Home & Emotional Bliss' },
      5: { name: 'Putra Bhava', desc: 'Children, Intelligence & Mantras' },
      6: { name: 'Satru Bhava', desc: 'Enemies, Health & Service' },
      7: { name: 'Kalatra Bhava', desc: 'Marriage, Spouse & Public Relations' },
      8: { name: 'Randhra Bhava', desc: 'Longevity, Transformation & Secrets' },
      9: { name: 'Bhagya Bhava', desc: 'Dharma, Higher Knowledge & Fortune' },
      10: { name: 'Karma Bhava', desc: 'Career, Public Status & Duty' },
      11: { name: 'Labha Bhava', desc: 'Gains, Income & Fulfillment' },
      12: { name: 'Vyaya Bhava', desc: 'Expenditure, Foreign Lands & Moksha' }
    }
  },
  hi: {
    appTitle: 'वेदांग ज्योतिष',
    appSubtitle: 'ब्रह्मांडीय विज्ञान (Celestial Science)',
    chartsTab: 'कुंडली चक्र',
    planetsTab: 'ग्रह एवं पंचांग',
    gocharTab: 'वर्तमान गोचर (ट्रांजिट)',
    shadbalaTab: 'षड्बल एवं बल',
    dashasTab: 'विंशोत्तरी दशाएं',
    doshasTab: 'दोष एवं योग',
    ashtakvargaTab: 'अष्टकवर्ग',
    varshphalTab: 'वर्षफल (वार्षिक)',
    prashnaTab: 'प्रश्न कुंडली',
    remediesTab: 'रत्न एवं उपाय',
    matchingTab: 'गुण मिलान',
    numerologyTab: 'अंक ज्योतिष (लो-शू)',
    palmistryTab: 'हस्तरेखा (सामुद्रिक)',
    vastuTab: 'वास्तु शास्त्र (दिशा ज्ञान)',
    aiTab: 'AI ज्योतिषी',
    northIndian: 'उत्तर भारतीय',
    southIndian: 'दक्षिण भारतीय',
    eastIndian: 'पूर्व भारतीय',
    singleView: '1 चार्ट',
    trioView: '3 चार्ट',
    quadView: '4 चार्ट',
    language: 'भाषा',
    house: 'भाव',
    lord: 'स्वामी',
    retrograde: 'वक्री',
    exalted: 'उच्च',
    debilitated: 'नीच',
    ownHouse: 'स्वगृही',
    combust: 'अस्त',
    rashi: 'राशि',
    points: 'अंक',
    showDegrees: 'अंश दिखाएं',
    hideDegrees: 'अंश छिपाएं',
    lagna: 'लग्न',
    moonSign: 'चंद्र राशि',
    nakshatra: 'नक्षत्र',
    famousProfiles: 'प्रसिद्ध कुंडलियां',
    saved: 'सहेजी गई कुंडली',
    createKundali: 'नई कुंडली',
    printReport: 'रिपोर्ट प्रिंट',
    period: 'अवधि',
    // Beneficence
    benefic: 'शुभ',
    malefic: 'अशुभ',
    neutral: 'तटस्थ',
    // Divisional Charts
    D1: 'लग्न कुंडली',
    Chandra: 'चंद्र कुंडली',
    Surya: 'सूर्य कुंडली',
    Transit: 'गोचर कुंडली',
    D2: 'होरा कुंडली',
    D3: 'द्रेष्काण कुंडली',
    D4: 'चतुर्थांश कुंडली',
    D7: 'सप्तमांश कुंडली',
    D9: 'नवांश कुंडली',
    D10: 'दशमांश कुंडली',
    D12: 'द्वादशांश कुंडली',
    D16: 'षोडशांश कुंडली',
    D20: 'विंशामंश कुंडली',
    D24: 'चतुर्विंशामंश कुंडली',
    D27: 'सप्तविंशामंश कुंडली',
    D30: 'त्रिंशांश कुंडली',
    D40: 'खवेदांश कुंडली',
    D45: 'अक्षवेदांश कुंडली',
    D60: 'षष्ट्यंश कुंडली',
    // House meanings
    houseMeanings: {
      1: { name: 'तनु भाव', desc: 'स्वयं, शारीरिक बनावट और जीवन का पथ' },
      2: { name: 'धन भाव', desc: 'धन, परिवार और वाणी' },
      3: { name: 'सहज भाव', desc: 'साहस, भाई-बहन और कौशल' },
      4: { name: 'सुख भाव', desc: 'माता, घर और भावनात्मक सुख' },
      5: { name: 'पुत्र भाव', desc: 'संतान, बुद्धि और मंत्र' },
      6: { name: 'शत्रु भाव', desc: 'शत्रु, रोग और सेवा' },
      7: { name: 'कलत्र भाव', desc: 'विवाह, जीवनसाथी और सार्वजनिक संबंध' },
      8: { name: 'रंध्र भाव', desc: 'आयु, परिवर्तन और रहस्य' },
      9: { name: 'भाग्य भाव', desc: 'धर्म, उच्च ज्ञान और भाग्य' },
      10: { name: 'कर्म भाव', desc: 'करियर, सार्वजनिक प्रतिष्ठा और कर्तव्य' },
      11: { name: 'लाभ भाव', desc: 'लाभ, आय और इच्छा पूर्ति' },
      12: { name: 'व्यय भाव', desc: 'व्यय, विदेश यात्रा और मोक्ष' }
    }
  }
};
